import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { isNative } from '@/lib/platform';
import { events } from '@/lib/platform/generated/bindings.gen';
import { sessionOpenTab } from '@/session/session-bridge';
import { resolveEntityKind } from '@/lib/sync/entity-store';
import { kindToTabType } from '@/hooks/navigation';

/**
 * Listens for the native `focus-doc` event the recording-hud emits when the
 * user clicks "Open notes" (via `focus_main_from_hud`). Opens the target entity
 * in the focused panel, resolving its kind from the entity store so a
 * recording's notes entity lands on the document view. Mounted once inside
 * SessionWrapper next to DeepLinkBridge.
 */
export function FocusDocBridge() {
  useEffect(() => {
    // The `focus-doc` event only exists on native (Tauri) — on web there is no
    // window.__TAURI_INTERNALS__, so listen() throws inside the Tauri API.
    // Bail out before touching it. (Sentry LUMEN-3Y)
    if (!isNative) return;

    let cancelled = false;
    let unlisten: (() => void) | undefined;
    (async () => {
      try {
        const u = await events.focusDoc.listen((event) => {
          const id = event.payload;
          sessionOpenTab(id, kindToTabType(resolveEntityKind(id)));
        });
        // If we already unmounted while awaiting, tear the listener down now —
        // the cleanup below captured `unlisten` while it was still undefined.
        if (cancelled) {
          u();
          return;
        }
        unlisten = u;
      } catch (e) {
        Sentry.captureException(e);
      }
    })();
    return () => {
      cancelled = true;
      unlisten?.();
    };
  }, []);
  return null;
}
